import { useState, useEffect, useMemo } from 'react';
import { Card, CardBody, Button, Spinner, Alert, Badge } from '@components/ui';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@components/ui';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  EnvelopeIcon,
  HomeIcon,
  ArrowUturnLeftIcon
} from '@heroicons/react/24/outline';
import { useBranchOffices, useDeleteBranchOffice, useReactivateBranchOffice } from '../hooks/useBranchOffices';
import { BranchOfficeFormModal } from '@/features/companies/components/BranchOfficeFormModal';
import type { BranchOffice } from '../types/branchOffice.types';
import Swal from 'sweetalert2';

export function BranchOfficeManagerPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState<BranchOffice | null>(null);

  // Debounce search to avoid losing focus
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log('⏰ Debounce: Updating debouncedSearch to:', search);
      setDebouncedSearch(search);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // Fetch ALL branch offices (with large pageSize to get all, without search param)
  const { data: allBranchesData, isLoading, error } = useBranchOffices({
    page: 1,
    pageSize: 9999 // Large number to get all branches
  });
  const deleteMutation = useDeleteBranchOffice();
  const reactivateMutation = useReactivateBranchOffice();

  // Client-side filtering and pagination
  const { paginatedBranches, totalFiltered } = useMemo(() => {
    if (!allBranchesData?.branchOffices) {
      return { paginatedBranches: [], totalFiltered: 0 };
    }

    const allBranches = allBranchesData.branchOffices;

    // Filter branches based on search query
    let filtered = allBranches;
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      filtered = allBranches.filter((branch) => {
        const name = branch.name.toLowerCase();
        const code = (branch.code || '').toLowerCase();
        const fiscalCode = (branch.fiscalCode || '').toLowerCase();
        const address = (branch.fullAddress || '').toLowerCase();

        return (
          name.includes(searchLower) ||
          code.includes(searchLower) ||
          fiscalCode.includes(searchLower) ||
          address.includes(searchLower)
        );
      });
    }

    // Paginate filtered results
    const pageSize = 10;
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginated = filtered.slice(startIndex, endIndex);

    console.log('🔍 BranchOfficeManagerPage - Client-side filtering:', {
      search,
      debouncedSearch,
      page,
      allBranches: allBranches.length,
      filtered: filtered.length,
      paginated: paginated.length,
    });

    return {
      paginatedBranches: paginated,
      totalFiltered: filtered.length,
    };
  }, [allBranchesData, debouncedSearch, page, search]);

  // Create data object compatible with existing pagination code
  const data = useMemo(() => {
    if (!allBranchesData) return undefined;

    const pageSize = 10;
    return {
      branchOffices: paginatedBranches,
      total: totalFiltered,
      page: page,
      pageSize: pageSize,
    };
  }, [allBranchesData, paginatedBranches, totalFiltered, page]);

  const handleOpenCreate = () => {
    setEditingBranch(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (branch: BranchOffice) => {
    setEditingBranch(branch);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingBranch(null);
  };

  const handleBranchOfficeCreated = () => {
    // The modal will handle closing itself after successful creation
    // The query will be invalidated automatically by the mutation
  };

  const handleDelete = async (branch: BranchOffice) => {
    if (branch.isMain) {
      Swal.fire({
        title: 'No permitido',
        text: 'No se puede eliminar la sucursal principal',
        icon: 'warning',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: `Se desactivará la sucursal "${branch.name}"`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, desactivar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      await deleteMutation.mutateAsync({ id: branch.id, companyId: branch.companyId });
      Swal.fire({
        title: 'Desactivada',
        text: 'La sucursal ha sido desactivada exitosamente',
        icon: 'success',
        confirmButtonText: 'Aceptar',
      });
    }
  };

  const handleReactivate = async (branch: BranchOffice) => {
    const result = await Swal.fire({
      title: '¿Reactivar sucursal?',
      text: `La sucursal "${branch.name}" volverá a estar activa.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#16a34a',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, reactivar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      await reactivateMutation.mutateAsync({ id: branch.id, companyId: branch.companyId });
      Swal.fire({
        title: 'Reactivada',
        text: 'La sucursal ha sido reactivada exitosamente',
        icon: 'success',
        confirmButtonText: 'Aceptar',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" title="Error al cargar sucursales">
        Ocurrió un error al cargar las sucursales. Por favor, intenta de nuevo.
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">Gestión de Sucursales</h1>
          <p className="mt-2 text-secondary-600 dark:text-secondary-400">
            Administra las sucursales de tu empresa
          </p>
        </div>
        <Button data-guide="Nueva Sucursal" className="w-full sm:w-auto" leftIcon={<PlusIcon className="w-5 h-5" />} onClick={handleOpenCreate}>
          Nueva Sucursal
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardBody className="p-4">
          <div className="space-y-2">
            <input
              type="text"
              placeholder="Buscar por nombre o código..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full max-w-md rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            {search !== debouncedSearch && search.length > 0 && (
              <p className="text-xs text-secondary-500 dark:text-secondary-400 flex items-center gap-1">
                <Spinner size="sm" />
                Buscando...
              </p>
            )}
            {debouncedSearch && search === debouncedSearch && (
              <p className="text-xs text-secondary-600 dark:text-secondary-400">
                {totalFiltered} resultado(s) encontrado(s)
              </p>
            )}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="p-0">
          {data?.branchOffices.length === 0 ? (
            <div className="text-center py-12">
              <BuildingOfficeIcon className="mx-auto h-12 w-12 text-secondary-400" />
              <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-white">
                No hay sucursales
              </h3>
              <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                {debouncedSearch ? `No se encontraron resultados para "${debouncedSearch}"` : 'Comienza creando tu primera sucursal'}
              </p>
              {!debouncedSearch && (
                <div className="mt-6">
                  <Button onClick={handleOpenCreate}>
                    <PlusIcon className="w-5 h-5 mr-2" />
                    Nueva Sucursal
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Ubicación</TableHead>
                    <TableHead>Contacto</TableHead>
                    <TableHead>Tipo Dirección</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.branchOffices.map((branch) => (
                    <TableRow key={branch.id}>
                      {/* Código */}
                      <TableCell className="font-medium">
                        <div className="flex flex-col">
                          <span className="font-semibold text-secondary-900 dark:text-white">
                            {branch.code}
                          </span>
                          {branch.fiscalCode && (
                            <span className="text-xs text-secondary-500 dark:text-secondary-400">
                              Fiscal: {branch.fiscalCode}
                            </span>
                          )}
                        </div>
                      </TableCell>

                      {/* Nombre */}
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium text-secondary-900 dark:text-white">
                            {branch.name}
                          </span>
                          {branch.warehousesCount !== undefined && branch.warehousesCount > 0 && (
                            <span className="text-xs text-secondary-500 dark:text-secondary-400 flex items-center gap-1 mt-1">
                              <HomeIcon className="w-3 h-3" />
                              {branch.warehousesCount} {branch.warehousesCount === 1 ? 'almacén' : 'almacenes'}
                            </span>
                          )}
                        </div>
                      </TableCell>

                      {/* Ubicación */}
                      <TableCell>
                        <div className="flex flex-col space-y-1 max-w-xs">
                          {branch.fullAddress ? (
                            <>
                              <div className="flex items-start gap-1">
                                <MapPinIcon className="w-4 h-4 text-secondary-400 mt-0.5 flex-shrink-0" />
                                <span className="text-sm text-secondary-700 dark:text-secondary-300 line-clamp-2">
                                  {branch.fullAddress}
                                </span>
                              </div>
                              {branch.coordinates?.latitude && branch.coordinates?.longitude && (
                                <span className="text-xs text-secondary-500 dark:text-secondary-400 ml-5">
                                  {branch.coordinates.latitude.toFixed(6)}, {branch.coordinates.longitude.toFixed(6)}
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-sm text-secondary-500 dark:text-secondary-400">
                              Sin dirección
                            </span>
                          )}
                        </div>
                      </TableCell>

                      {/* Contacto */}
                      <TableCell>
                        <div className="flex flex-col space-y-1">
                          {branch.email ? (
                            <div className="flex items-center gap-1 text-sm text-secondary-600 dark:text-secondary-400">
                              <EnvelopeIcon className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate max-w-[200px]">{branch.email}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-secondary-400 dark:text-secondary-500">
                              Sin contacto
                            </span>
                          )}
                        </div>
                      </TableCell>

                      {/* Tipo de Dirección */}
                      <TableCell>
                        {branch.addressType ? (
                          <Badge variant="default" size="sm">
                            {branch.addressType}
                          </Badge>
                        ) : (
                          <span className="text-xs text-secondary-400">-</span>
                        )}
                      </TableCell>

                      {/* Tipo de Sucursal */}
                      <TableCell>
                        {branch.isMain ? (
                          <Badge variant="primary" size="sm">
                            Principal
                          </Badge>
                        ) : (
                          <Badge variant="default" size="sm">
                            Sucursal
                          </Badge>
                        )}
                      </TableCell>

                      {/* Estado */}
                      <TableCell>
                        {branch.isActive ? (
                          <Badge variant="success" size="sm">
                            Activa
                          </Badge>
                        ) : (
                          <Badge variant="danger" size="sm">
                            Inactiva
                          </Badge>
                        )}
                      </TableCell>

                      {/* Acciones */}
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {branch.isActive && (
                            <button
                              onClick={() => handleOpenEdit(branch)}
                              className="text-primary-600 hover:text-primary-900 dark:hover:text-primary-400 transition-colors"
                              title="Editar sucursal"
                            >
                              <PencilIcon className="w-5 h-5" />
                            </button>
                          )}
                          {branch.isActive && !branch.isMain && (
                            <button
                              onClick={() => handleDelete(branch)}
                              className="text-danger-600 hover:text-danger-900 dark:hover:text-danger-400 transition-colors"
                              title="Desactivar sucursal"
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          )}
                          {!branch.isActive && (
                            <button
                              onClick={() => handleReactivate(branch)}
                              className="text-success-600 hover:text-success-700 dark:hover:text-success-500 transition-colors"
                              title="Reactivar sucursal"
                            >
                              <ArrowUturnLeftIcon className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Paginación */}
      {data && data.branchOffices.length > 0 && (
        <Card>
          <CardBody className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <p className="text-sm text-secondary-700 dark:text-secondary-300">
                  Mostrando {(page - 1) * data.pageSize + 1} a{' '}
                  {Math.min(page * data.pageSize, data.total)} de {data.total} sucursales
                </p>
                <div className="text-xs text-secondary-500 dark:text-secondary-400">
                  Página {page} de {Math.ceil(data.total / data.pageSize)}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page * data.pageSize >= data.total}
                  onClick={() => setPage(page + 1)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Modal Create/Edit Branch Office */}
      <BranchOfficeFormModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onBranchOfficeCreated={handleBranchOfficeCreated}
        branchOffice={editingBranch}
      />
    </div>
  );
}
